/**
 * useCurrentTeamMember
 *
 * WEB-002 follow-up: GET /api/users/{teamMemberId} for the LOGGED-IN user's
 * own record was firing twice on a single dashboard load — AuthContext's
 * `loadTimeoutSettings()` ran a raw `api.get()` (no React Query, no cache) to
 * read `uiPreferences.doNotAutoTimeout`, while `useAllowJournaling()` ran its
 * own independent `useQuery({ queryKey: ['user', teamMemberId, 'journaling-pref'] })`
 * to read `uiPreferences.allowJournaling` — two separate cache entries (one not
 * even React Query) for the exact same endpoint and the exact same response.
 *
 * This mirrors the `/api/company` fix in `useCompany.ts`: one shared queryKey
 * + queryFn + staleTime, so every caller either shares the same React Query
 * cache entry (hook callers) or reads/populates it via `queryClient.fetchQuery`
 * (non-hook callers, e.g. AuthContext). Deliberately scoped to a distinct
 * `'self'` key segment rather than reusing `['user', memberId]` (used by
 * TeamEditPanel/TeamFullPageDetail for arbitrary — not necessarily
 * self — team members) so this fix can't change that unrelated cache's
 * staleTime/gcTime behavior.
 *
 * @module hooks/useCurrentTeamMember
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { api } from '@/lib/api'

/** Loosely-typed shape of the /api/users/{id} response — callers narrow to the fields they need. */
export interface CurrentTeamMemberProfile {
  uiPreferences?: Record<string, any>
  [key: string]: any
}

/** Shared queryKey for the logged-in user's own team_member record. */
export const currentTeamMemberQueryKey = (teamMemberId: string) => ['user', teamMemberId, 'self'] as const

/** 5 min — mirrors COMPANY_STALE_TIME; edits (e.g. TeamEditPanel saving the same id) invalidate `['user', ...]` explicitly. */
export const CURRENT_TEAM_MEMBER_STALE_TIME = 5 * 60 * 1000
export const CURRENT_TEAM_MEMBER_GC_TIME = 10 * 60 * 1000

/**
 * Plain query-options object for callers that aren't inside a component —
 * e.g. `queryClient.fetchQuery(currentTeamMemberQueryOptions(teamMemberId))`.
 */
export function currentTeamMemberQueryOptions(teamMemberId: string) {
  return {
    queryKey: currentTeamMemberQueryKey(teamMemberId),
    queryFn: () => api.get<CurrentTeamMemberProfile>(`/api/users/${teamMemberId}`),
    staleTime: CURRENT_TEAM_MEMBER_STALE_TIME,
    gcTime: CURRENT_TEAM_MEMBER_GC_TIME,
  }
}

/**
 * @param teamMemberId  The logged-in user's own team_member id (`auth.user.teamMemberId`).
 * @param options Optional overrides merged on top of the shared defaults — queryKey/staleTime/gcTime are never overridable, so every observer stays on the same cache entry.
 */
export function useCurrentTeamMember<T = CurrentTeamMemberProfile>(
  teamMemberId: string | null | undefined,
  options?: Partial<Omit<UseQueryOptions<T>, 'queryKey' | 'staleTime' | 'gcTime'>>
) {
  return useQuery<T>({
    queryFn: () => api.get<T>(`/api/users/${teamMemberId}`),
    ...options,
    queryKey: currentTeamMemberQueryKey(teamMemberId || ''),
    enabled: !!teamMemberId && (options?.enabled ?? true),
    staleTime: CURRENT_TEAM_MEMBER_STALE_TIME,
    gcTime: CURRENT_TEAM_MEMBER_GC_TIME,
  })
}
